<template>
  <Loader v-if="isDataFetching" />
  <div v-else :class="$options.name">
    <v-card :loading="isLayerLoading">
      <v-tabs
        :value="selectedTab"
        grow
        show-arrows
      >
        <v-tabs-slider />
        <template
          v-for="(tab, index) in activeTabs"
        >
          <v-tab
            v-if="tab.permit"
            :key="`tab-${tab.id}`"
            :value="tab.id"
            @click.native.prevent.stop.capture="changeTab(index)"
          >
            {{ tab.title }}
          </v-tab>
        </template>
      </v-tabs>
      <v-tabs-items>
        <div v-if="selectedTabId === 0">
          <v-card-text>
            <!--  тут в форму передается isEdit - всегда true-->
            <!-- а это значит что некотоыре элементы 100% в disabled  -->
            <layer-form
              :data="selectedLayer"
              :icon-list="iconList"
              :category-list="categoryList"
              :is-loading="isLayerLoading"
              is-edit
              @registerGuard="registerGuards($event, 0)"
              @submit="onLayerUpdate"
              @export="handleExport"
              @syncLayer="handleSync"
            />
          </v-card-text>
        </div>

        <geometry-table
          v-if="selectedTabId === 1"
          :id="id"
          :value-list="valueList"
          :layer-settings="layerSettings"
          :selected-layer="selectedLayer"
          :layer-permissions="layerPermissions"
        />

        <tms-tab
          v-if="selectedTabId === 2"
          :value="layerSettings"
          @registerGuard="registerGuards($event, selectedTabId)"
          @save="onLayerSettingsSave"
        />

        <div v-if="selectedTabId === 3">
          <external-url-tab
            :settings-prop="layerSettings"
            :icon="selectedLayer.icon"
            @registerGuard="registerGuards($event, 3)"
            @save="onLayerSettingsSave"
          />
        </div>
        <div v-if="selectedTabId === 4">
          <v-card-text>
            <object-view-settings-form
              :value="layerSettings"
              :form="objectFormSingle"
              :icons="iconList"
              :icon="selectedLayer.icon"
              :map-type="selectedLayer && selectedLayer.mapType"
              @registerGuard="registerGuards($event, 4)"
              @save="onLayerSettingsSave"
            />
          </v-card-text>
        </div>

        <div v-if="selectedTabId === 5">
          <attachments-tab
            :single="selectedLayer"
            @changeLayerLoadingState="changeLayerLoadingState"
            @update="refreshLayer"
          />
        </div>

        <div v-if="selectedTabId === 6">
          <v-card-text>
            <orthophotomap-form
              :object-id="selectedLayer.id"
              :has-map="!!selectedLayer.orthophotomap"
              :builded-map="selectedLayer.orthophotomap && selectedLayer.orthophotomap.builded"
              :fit="bbox"
              object-type="layers"
            />
          </v-card-text>
        </div>

        <div v-if="selectedTabId === 7">
          <model-tab
            :single="selectedLayer"
            @changeLayerLoadingState="changeLayerLoadingState"
          />
        </div>

        <div v-if="selectedTabId === 8">
          <object-form-create-form
            :data="objectFormSingle"
            is-edit
            @registerGuard="registerGuards($event, 8)"
            @submit="onObjectFormUpdate"
          />
        </div>

        <div v-if="selectedTabId === 9">
          <value-list-tab
            :list="valueList"
            :layer-id="id"
            :axis-lines="selectedLayer.layerSettings ? selectedLayer.layerSettings.axisLines : []"
            @update="onValueListUpdate"
          />
        </div>

        <div v-if="selectedTabId === 10">
          <v-card-text>
            <external-connection-form
              :data="selectedLayer.externalConnectionSettings"
              :form-data="objectFormSingle"
              :has-settings="!!selectedLayer.externalConnectionSettings"
              :map-type="selectedLayer.mapType"
              @registerGuard="registerGuards($event, 10)"
              @update="onExternalConnectionSettingsUpdate"
            />
          </v-card-text>
        </div>

        <div v-if="selectedTabId === 11">
          <custom-tab
            :prop-settings="layerSettings"
            @registerGuard="registerGuards($event, 11)"
            @save="onLayerSettingsSave"
          />
        </div>

        <div v-if="selectedTabId === 12">
          <v-card-text>
            <import-data-tab
              ref="importTab"
              :layer-permissions="layerPermissions"
              :layer-object="selectedLayer"
            />
          </v-card-text>
        </div>
        <div
          v-if="selectedTabId === 13"
          :key="'actionList'"
        >
          <v-card-text>
            <v-card-title class="pa-0">
              <h5>Журнал событий</h5>
            </v-card-title>

            <v-data-table
              :headers="actionColumns"
              :items="actionList"
              class="elevation-1"
              hide-default-footer
            />
          </v-card-text>
        </div>
      </v-tabs-items>
    </v-card>

    <event-history-elements
      v-if="selectedLayer.mapType === 'history'"
      :event-types="MAP_TYPES"
      :single="selectedLayer"
      @onChildRemove="onChildRemove"
    />

    <confirmation-dialog
      name="confirmation-dialog"
      :dialog="true"
      persistent
      @confirmed="handleConfirmed(tempTabId)"
      @canceled="handleCanceled"
    >
      <template v-slot:title>
        Ваши данные могут быть утреняны!
      </template>

      <template v-slot:text>
        Сохраните ваши данные  перед тем как перейдете на другую страницу
      </template>
    </confirmation-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import moment from 'moment';
import { MAP_TYPES } from '@/utils/object-types';
import LayerForm from '@/components/page/layers/forms/LayerForm.vue';
import ExternalConnectionForm from '@/components/page/layers/forms/ExternalConnectionForm.vue';
import ObjectFormCreateForm from '@/views/settings/object-form/components/ObjectForm.vue';
import OrthophotomapForm from '@/components/common/forms/OrthophotomapForm.vue';
import EventHistoryElements from '@/views/events/components/EventHistoryElements.vue';
import ObjectViewSettingsForm from '@/components/page/view-settings/ObjectViewSettingsForm.vue';
import GeometryTable from '@/views/layers/components/GeometryTable.vue';
import ModelTab from '@/views/layers/components/tabs/ModelTab.vue';
import AttachmentsTab from '@/views/layers/components/tabs/AttachmentsTab.vue';
import ValueListTab from '@/views/layers/components/tabs/ValueListTab.vue';
import CustomTab from '@/views/layers/components/tabs/CustomTab.vue';
import ImportDataTab from '@/views/layers/components/tabs/ImportDataTab.vue';
import TmsTab from '@/views/layers/components/tabs/TmsTab.vue';
import ExternalUrlTab from '@/views/layers/components/tabs/ExternalUrlTab.vue';
import ConfirmationDialog from '@/components/dialogs/confirmations/ConfirmationDialog.vue';
import Loader from '@/components/common/Loader.vue';

import { downloadBlobFile } from '@/utils/download-file';
import isClosedHost from '@/utils/is-closed-host';
import { deepEqual } from '@/utils/object';

import { getSelectLayer, getValueList, saveLayerSettings } from '@/services/layers';
import systemService from '@/services/system';

const TABS = {
  visual: ['polygon', 'linestring', 'point'],
  model: ['model'],
  orthophotomap: ['orthophotomap'],
  attachments: ['polygon', 'linestring', 'point'],
  form: ['polygon', 'linestring', 'point'],
  valueList: ['region'],
  tms: ['tms'],
  externalUrl: ['external-url'],
  custom: ['custom'],
  import: ['polygon', 'linestring', 'point'],
  actionList: [],
  geometries: ['point', 'polygon', 'linestring', 'region'],
};

export default {
  name: 'layers-item-view',
  components: {
    Loader,
    ConfirmationDialog,
    GeometryTable,
    TmsTab,
    ExternalUrlTab,
    ValueListTab,
    ModelTab,
    AttachmentsTab,
    OrthophotomapForm,
    ExternalConnectionForm,
    ObjectFormCreateForm,
    LayerForm,
    EventHistoryElements,
    ObjectViewSettingsForm,
    CustomTab,
    ImportDataTab,
  },
  props: {
    id: String,
    title: String,
  },
  data: () => ({
    isDataFetching: true,
    selectedLayer: {},
    layerPermissions: {},
    valueList: [],
    selectedTab: 0,
    selectedTabId: 0,
    tempTabId: null,
    layerSettings: {},
    tabs: [],
    isLayerLoading: false,
    actionColumns: [
      {
        value: 'action',
        text: 'Тип события',
        sortable: false,
      },
      {
        value: 'name',
        text: 'Имя пользователя',
        sortable: false,
      },
      {
        value: 'email',
        text: 'Почта',
        sortable: false,
      },
      {
        value: 'roles',
        text: 'Роли',
        sortable: false,
      },
      {
        value: 'dateTime',
        text: 'Дата и время',
        sortable: false,
      },
    ],

    externalConnectionSettingsIsLoading: false,
  }),
  computed: {
    ...mapState('icons', {
      iconList: 'fullList',
    }),
    ...mapState('categories', {
      categoryList: 'fullList',
    }),

    ...mapState('objectForms', {
      objectFormSingle: 'single',
    }),

    ...mapGetters(['permissions']),

    hasPermission() {
      return (perm) => this.permissions.includes(perm);
    },

    bbox() {
      if (!this.selectedLayer.orthophotomap) return [];
      return this.selectedLayer.orthophotomap.bbox;
    },

    activeTabs() {
      return Object.values(this.tabs).filter(tab => tab.permit);
    },

    actionList() {
      const items = [];

      if (this.selectedLayer.createdBy) {
        const { name, email, roles } = this.selectedLayer.createdBy;

        items.push({
          action: 'Создал слой',
          name,
          email,
          roles: roles.map(role => role.name).join(', '),
        });
      }

      if (this.selectedLayer.updatedBy) {
        const { name, email, roles } = this.selectedLayer.updatedBy;

        items.push({
          action: 'Последнее редактирование',
          name,
          email,
          roles: roles.map(role => role.name).join(', '),
          dateTime: moment(this.selectedLayer.updatedAt.date).format('DD.MM.YYYY HH:mm'),
        });
      }

      return items;
    },
  },
  async created() {
    await this.getSelectLayerData();
    // Колхоз
    this.$root.$emit('updateHeader', this.selectedLayer.name);

    const actions = [
      this.$store.dispatch('icons/getFullList'),
      this.$store.dispatch('categories/getFullList'),
      this.$store.dispatch('filters/getAvailable'),
      this.$store.dispatch('layerGroups/getFullList'),
    ];

    if (this.selectedLayer.form) {
      this.objectFormGetItem(this.selectedLayer.form.id);
    } else if (['point', 'polygon', 'linestring'].includes(this.selectedLayer.mapType)) {
      actions.push(this.$store.dispatch('objectForms/getFullList'));
    }

    if (this.selectedLayer.mapType === 'region') {
      // ValueList - похож на legend. так как там передается цветовое обозначение
      this.valueList = await getValueList(this.id);
    }

    this.MAP_TYPES = MAP_TYPES;
    this.layerSettings = {
      ...this.selectedLayer.layerSettings,
    };

    await Promise.all(actions);
    this.createTabs();
    this.isDataFetching = false;
  },
  methods: {
    registerGuards($event, id) {
      const asArray = Array.isArray($event) ? $event : Array.prototype.slice.call($event);
      const [startValues, fn] = asArray;
      const findTab = this.tabs[id];
      if (findTab) {
        findTab.clearValues = startValues;
        findTab.guardFn = fn;
      }
    },
    createTabs() {
      this.tabs = {
        0: {
          id: 0,
          title: 'Основная информация',
          oldValue: 'main',
          permit: true,
          component: LayerForm,
        },
        1: {
          id: 1,
          title: 'Объекты слоя',
          oldValue: 'geometries',
          permit: TABS.geometries.includes(this.selectedLayer.mapType),
        },
        2: {
          id: 2,
          title: 'Настройка TMS',
          oldValue: 'tms',
          permit: TABS.tms.includes(this.selectedLayer.mapType),
        },
        3: {
          id: 3,
          title: 'Настройка слоя по внешней ссылке',
          oldValue: 'externalUrl',
          permit: TABS.externalUrl.includes(this.selectedLayer.mapType),
        },
        4: {
          id: 4,
          title: 'Настройка отображения слоя',
          oldValue: 'visual',
          permit: TABS.visual.includes(this.selectedLayer.mapType),
        },
        5: {
          id: 5,
          title: 'Прикрепленные файлы к слою',
          oldValue: 'attachments',
          permit: TABS.attachments.includes(this.selectedLayer.mapType),
        },
        6: {
          id: 6,
          title: 'Снимок с воздуха',
          oldValue: 'orthophotomap',
          permit: TABS.orthophotomap.includes(this.selectedLayer.mapType),
        },
        7: {
          id: 7,
          title: 'Модель события',
          oldValue: 'model',
          permit: TABS.model.includes(this.selectedLayer.mapType),
        },
        8: {
          id: 8,
          title: 'Поля объектов слоя',
          oldValue: 'props',
          permit: TABS.form.includes(this.selectedLayer.mapType) && this.selectedLayer.form,
        },
        9: {
          id: 9,
          title: 'Преднастроенные значения',
          oldValue: 'valueList',
          permit: TABS.valueList.includes(this.selectedLayer.mapType),
        },
        10: {
          id: 10,
          title: 'Настройка источника данных',
          oldValue: '',
          permit: this.selectedLayer.hasExternalConnection,
        },
        11: {
          id: 11,
          title: 'Настройка слоя',
          oldValue: '',
          permit: TABS.custom.includes(this.selectedLayer.mapType),
        },
        12: {
          id: 12,
          title: 'Импорт',
          oldValue: '',
          permit: TABS.import.includes(this.selectedLayer.mapType) && this.layerPermissions['geometry-write'],
        },
        13: {
          id: 13,
          title: 'Журнал событий',
          oldValue: '',
          permit: this.actionList.length,
        },
      };
    },

    handleConfirmed(index) {
      const { id } = this.activeTabs[index];
      this.selectedTabId = id;
      this.selectedTab = index;
      this.tempTabId = null;
    },

    changeTab(index) {
      const findTab = this.activeTabs[this.selectedTab];
      if (findTab && 'guardFn' in findTab && !deepEqual(findTab.clearValues, findTab.guardFn())) {
        this.$dialog.show('confirmation-dialog');
        this.tempTabId = index;
        return;
      }

      this.handleConfirmed(index);
    },

    handleCanceled() {
      this.$dialog.hide('confirmation-dialog');
    },
    ...mapActions(['showNotify']),
    ...mapActions('layer-objects', [
      'update',
      'getItem',
    ]),
    ...mapActions('layer-objects/geometries', {
      getGeometriesList: 'getList',
      getRegionDataList: 'getRegionDataList',
    }),

    ...mapActions('objectForms', {
      objectFormGetItem: 'getItem',
      objectFormUpdate: 'update',
      objectFormRemoveSingle: 'removeSingle',
    }),

    ...mapActions('layersSync', ['setLayerSyncState']),

    ...mapActions('externalConnections', ['saveSettingsForLayerObject']),

    handleError(e) {
      const defaultMessage = e.message || 'Что-то пошло не так';
      const dataMessage = e.response?.data?.data?.message;

      this.showNotify({ type: 'error', message: dataMessage || defaultMessage });
    },

    async getSelectLayerData() {
      try {
        const selectLayer = await getSelectLayer(this.id);

        this.selectedLayer = selectLayer?.data || {};
        this.layerPermissions = selectLayer?.permissions || {};
      } catch (e) {
        this.handleError(e);
      }
    },

    async onLayerUpdate(data) {
      this.isLayerLoading = true;

      try {
        await this.update({ id: this.id, ...data });

        await this.setSyncSettings(data);

        await this.getSelectLayerData();

        if (this.selectedLayer.formId) {
          await this.objectFormGetItem(this.selectedLayer.formId);
        } else {
          await this.objectFormRemoveSingle();
        }

        this.showNotify({ message: 'Данные успешно обновлены' });
      } catch (e) {
        this.handleError(e);
      }

      this.isLayerLoading = false;
    },

    async refreshLayer() {
      await this.getSelectLayerData();
    },

    connectToWs(id) {
      this.$echo.channel(`.object-sync.${id}`)
        .listen('.LayerObjectSyncStatus', (data) => {
          console.log(data);
        });
    },

    async handleSync(ids) {
      try {
        const res = await systemService.syncLayers([ids]);

        if (this.$echo.connector?.socket?.connected) {
          console.log('connect');
          this.connectToWs(res.id);
        }
        this.showNotify({ type: 'success', message: 'Синхронизация слоя прошла успешно' });
      } catch (error) {
        this.showNotify({ type: 'error', message: 'Не удалось синхронизировать слой' });
      }
    },

    async handleExport(id) {
      try {
        const file = await systemService.exportLayer(id);

        if (file) {
          downloadBlobFile(file, 'sync-data.json');
        }
        this.showNotify({ type: 'success', message: 'Скачивание прошло успешно' });
      } catch (error) {
        this.showNotify({ type: 'error', message: 'Не удалось экспортировать слой' });
      }
    },

    async setSyncSettings(data) {
      const { isSyncAvailable, isSyncEnable } = this.selectedLayer?.sync;
      const newIsSyncEnable = data?.sync?.isSyncEnable;

      if (!isClosedHost() || !isSyncAvailable || !this.hasPermission('layerSynchronization')) return;
      if (newIsSyncEnable === isSyncEnable) return;

      try {
        await this.setLayerSyncState({
          id: this.selectedLayer.id,
          isSyncEnable: newIsSyncEnable,
        });
      } catch (e) {
        this.showNotify({ type: 'error', message: 'Не удалось сохраниться настройки синхронизации' });
      }
    },

    layerSettingsUpdate(event) {
      this.layerSettings = event;
    },

    async onLayerSettingsSave(data) {
      this.layerSettings = data;
      this.isLayerLoading = true;

      try {
        await saveLayerSettings(this.id, this.layerSettings);
        this.showNotify({ message: 'Настройки слоя успешно обновлены' });
      } catch (e) {
        this.handleError(e);
      } finally {
        this.isLayerLoading = false;
      }
    },

    async onValueListUpdate(list) {
      this.valueList = list;
      await this.getSelectLayerData();
    },

    changeLayerLoadingState(state) {
      this.isLayerLoading = state;
    },

    async onObjectFormUpdate(data) {
      const iconTextFromId = this.layerSettings?.iconView?.iconTextFrom?.id;
      if (iconTextFromId) {
        const iconViewFormField = data.fields.find(({ id }) => (id === iconTextFromId));

        if (iconViewFormField) {
          this.layerSettings.iconView.iconTextFrom = { ...iconViewFormField };
        }
      }

      this.isLayerLoading = true;
      try {
        await this.objectFormUpdate(data);
        this.showNotify({ message: 'Форма успешно обновлена' });
      } catch (e) {
        this.handleError(e);
      } finally {
        this.isLayerLoading = false;
        await this.onLayerSettingsSave(this.layerSettings);
        await this.getSelectLayerData();
        this.getGeometries();
      }
    },

    async getGeometries() {
      if (this.selectedLayer.mapType === 'region') {
        await this.getRegionDataList({ objectId: this.selectedLayer.id });
      } else {
        await this.getGeometriesList({ objectId: this.id }, this.id);
      }
    },

    async onChildRemove() {
      this.isLayerLoading = true;
      await this.getSelectLayerData();
      this.isLayerLoading = false;
    },

    async onExternalConnectionSettingsUpdate(data) {
      this.externalConnectionSettingsIsLoading = true;
      try {
        await this.saveSettingsForLayerObject({
          layerId: this.selectedLayer.id,
          data,
        });
        this.showNotify({ message: 'Настройки сохранены' });
      } catch (e) {
        this.handleError(e);
      }
      await this.getSelectLayerData();
      this.externalConnectionSettingsIsLoading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.layers-item-view {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
